"use client";

import { Suspense, useState } from "react";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { useQuery } from "@tanstack/react-query";
import { Eye, MessageSquare } from "lucide-react";
import { Button } from "@/app/_module/components/ui/button";
import { forumService, timeAgo, formatScore, forumPath } from "../_modules/components/ForumService";
import { CategoryRail, PostFeedSkeleton, ForumEmptyState } from "../_modules/components/ForumChrome";
import { ForumSearchBox } from "../_modules/components/ForumSearchBox";
import { CategoryChip } from "../_modules/components/ForumBadges";
import { AuthorName } from "../_modules/components/KarmaHovercard";
import { VoteControl } from "../_modules/components/VoteControl";

const WHERE: Record<string, string> = {
  title: "in the title",
  body: "in the post",
  replies: "in the replies",
  author: "by this agent",
  category: "in this room",
  close: "close spelling",
};

function Highlight({ text, terms }: { text: string; terms: string[] }) {
  const clean = terms.map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).filter(Boolean);
  if (!clean.length || !text) return <>{text}</>;
  const re = new RegExp(`(${clean.join("|")})`, "ig");
  return (
    <>
      {text.split(re).map((part, i) =>
        re.test(part) ? (
          <mark key={i} className="rounded-sm bg-yellow-100 px-0.5 text-foreground dark:bg-yellow-900/50">
            {part}
          </mark>
        ) : (
          <span key={i}>{part}</span>
        ),
      )}
    </>
  );
}

function SearchInner() {
  const params = useSearchParams();
  const q = (params.get("q") || "").trim();
  const [page, setPage] = useState(0);
  const query = useQuery({
    queryKey: ["forum", "search", q, page],
    queryFn: () => forumService.search(q, page),
    enabled: q.length >= 2,
    staleTime: 60_000,
  });
  const hits = query.data?.posts || [];
  const terms = query.data?.terms?.length ? query.data.terms : q ? [q] : [];

  return (
    <div className="flex gap-6">
      <aside className="hidden w-52 shrink-0 lg:block">
        <div className="sticky top-3">
          <p className="px-2 pb-2 font-mono text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">Rooms</p>
          <CategoryRail activeSlug={null} />
        </div>
      </aside>
      <main className="min-w-0 flex-1">
        <nav className="font-mono text-[11px] uppercase tracking-[0.12em] text-muted-foreground" aria-label="Breadcrumb">
          <Link href={forumPath()} className="hover:text-primary">Community</Link>
          <span className="mx-1">/</span>
          <span className="text-foreground/80">Search</span>
        </nav>
        <h1 className="forum-serif mt-1 text-[22px] font-medium leading-tight text-foreground">
          {q ? <>Results for &ldquo;{q}&rdquo;</> : "Search the floor"}
        </h1>
        <p className="mt-0.5 font-mono text-[11px] text-muted-foreground">
          {q && query.data
            ? `${hits.length}${query.data.hasMore ? "+" : ""} ${hits.length === 1 ? "thread" : "threads"}${page > 0 ? ` · page ${page + 1}` : ""}`
            : "Titles, posts and replies. Quotes for exact phrases, a minus to leave a word out."}
        </p>
        <ForumSearchBox className="mt-3 max-w-xl" />

        <div className="mt-4">
          {q.length < 2 ? null : query.isLoading ? (
            <PostFeedSkeleton />
          ) : query.isError ? (
            <ForumEmptyState title="Couldn't search the floor" subtitle={(query.error as Error)?.message} />
          ) : hits.length === 0 ? (
            <ForumEmptyState title="Nothing on the floor for that yet." subtitle="Try a carrier, a condition or a vendor by name. Or bring it up." />
          ) : (
            <div className="divide-y rounded-lg border bg-card">
              {hits.map((p) => (
                <div key={p.id} className="flex gap-3 px-3 py-3 sm:px-4">
                  <div className="shrink-0 self-start">
                    <VoteControl targetType="post" targetId={p.id} score={p.score} myVote={p.myVote} size="sm" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-muted-foreground">
                      <CategoryChip name={p.category.name} slug={p.category.slug} />
                      <AuthorName author={p.author} />
                      <span className="font-mono tabular-nums">{timeAgo(p.createdAt)}</span>
                    </div>
                    <Link href={forumPath(`/t/${p.slug}`)} className="forum-serif mt-1 block text-[16px] font-medium leading-snug text-foreground hover:text-primary sm:text-[17px]">
                      <Highlight text={p.title} terms={terms} />
                    </Link>
                    {p.snippet && (
                      <p className="mt-0.5 text-[13px] leading-5 text-muted-foreground">
                        <Highlight text={p.snippet} terms={terms} />
                      </p>
                    )}
                    <div className="mt-1.5 flex flex-wrap items-center gap-x-3 font-mono text-[11px] tabular-nums text-muted-foreground">
                      <span>matched {WHERE[p.matchedIn] || "here"}{p.matchedIn === "replies" && p.replyHits > 1 ? ` (${p.replyHits})` : ""}</span>
                      <span className="inline-flex items-center gap-1"><MessageSquare className="h-3 w-3" />{formatScore(p.commentCount)}</span>
                      <span className="hidden items-center gap-1 sm:inline-flex"><Eye className="h-3 w-3" />{formatScore(p.viewCount)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          {q.length >= 2 && query.data && (query.data.hasMore || page > 0) && (
            <div className="mt-3 flex gap-2">
              {page > 0 && (
                <Button size="sm" variant="outline" onClick={() => setPage((p) => p - 1)}>
                  Previous
                </Button>
              )}
              {query.data.hasMore && (
                <Button size="sm" variant="outline" onClick={() => setPage((p) => p + 1)}>
                  More results
                </Button>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

export default function ForumSearchPage() {
  return (
    <Suspense fallback={<PostFeedSkeleton />}>
      <SearchInner />
    </Suspense>
  );
}
